/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { GenerativeContentBlob, Part } from "@google/generative-ai";
import { EventEmitter } from "eventemitter3";
import {
  ServerContent,
  StreamingLog,
  ToolCall,
  ToolCallCancellation,
  ToolResponseMessage,
  type LiveConfig,
} from "../multimodal-live-types";
import { base64ToArrayBuffer } from "./utils";
import { GeminiInterviewDetailsProps } from "../types/interviews";
import { getBackendWsUrl } from "../constants/backend";
import { v4 as uuidv4 } from 'uuid';

// Global WebSocket instance to be shared across all components
export const globalWsInstance = {
  ws: null as WebSocket | null,
  interviewSessionId: '',
  url: '',
  isConnecting: false,
  config: null as any,
  connectionLock: false,
  lockUntil: 0,
  lastConnectionTime: 0,
  connectionCount: 0
};

/**
 * the events that this client will emit
 */
interface MultimodalLiveClientEventTypes {
  open: () => void;
  log: (log: StreamingLog) => void;
  close: (event: CloseEvent) => void;
  audio: (data: ArrayBuffer) => void;
  content: (data: ServerContent) => void;
  interrupted: () => void;
  setupcomplete: () => void;
  turncomplete: () => void;
  toolcall: (toolCall: ToolCall) => void;
  toolcallcancellation: (toolcallCancellation: ToolCallCancellation) => void;
  transcription: (text: string) => void;
  input_transcription: (text: string) => void;
  cleanup_audio: () => void;
  thinking: (isThinking: boolean) => void;
}

export type MultimodalLiveAPIClientConnection = {
  url?: string;
  apiKey: string;
  interviewDetails: GeminiInterviewDetailsProps;
  implementorUniqueId?: string; // For implementor interviews
};

/**
 * A event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 */
export class MultimodalLiveClient extends EventEmitter<MultimodalLiveClientEventTypes> {
  public ws: WebSocket | null = null;
  protected config: LiveConfig | null = null;
  public url: string = "";
  private interviewSessionId: string;
  private _lastTranscription: string = ''; // Track the last transcription for delta tracking
  private connectionAttempts: number = 0; // Track connection attempts
  
  // Static properties for tracking quota
  private static quotaExceeded: boolean = false;
  
  // Check if quota exceeded has been detected
  static isQuotaExceeded(): boolean {
    return MultimodalLiveClient.quotaExceeded;
  }
  
  // Set the quota exceeded flag
  static setQuotaExceeded(value: boolean = true): void {
    console.log(`[MultimodalLiveClient] Setting quota exceeded to ${value}`);
    MultimodalLiveClient.quotaExceeded = value;
    
    if (value) {
      // Dispatch an event when quota is exceeded
      try {
        const quotaEvent = new CustomEvent('geminiQuotaExceeded', { detail: { message: 'Quota exceeded' } });
        window.dispatchEvent(quotaEvent);
      } catch (err) {
        console.error('[MultimodalLiveClient] Error dispatching quota event:', err);
      }
    }
  }
  
  // Reset the quota exceeded flag
  static resetQuotaExceeded(): void {
    console.log('[MultimodalLiveClient] Resetting quota exceeded flag');
    MultimodalLiveClient.quotaExceeded = false;
  }
  
  public getConfig() {
    return { ...this.config };
  }

  private implementorUniqueId?: string;

  constructor({ url, apiKey, interviewDetails, implementorUniqueId }: MultimodalLiveAPIClientConnection) {
    super();
    this.implementorUniqueId = implementorUniqueId;
    
    // Generate or retrieve a persistent client ID for backend connection
    // First try to get from session storage to persist across page reloads
    let persistentClientId = '';
    try {
      persistentClientId = sessionStorage.getItem('mocktrail_websocket_client_id') || '';
      
      // If no ID exists in storage, generate a new one and store it
      if (!persistentClientId) {
        persistentClientId = uuidv4();
        sessionStorage.setItem('mocktrail_websocket_client_id', persistentClientId);
        console.log(`[MultimodalLiveClient] Generated new client ID: ${persistentClientId}`);
      } else {
        console.log(`[MultimodalLiveClient] Using existing client ID: ${persistentClientId}`);
      }
    } catch (err) {
      // If session storage is not available, fall back to a new ID
      persistentClientId = uuidv4();
      console.log(`[MultimodalLiveClient] Cannot use session storage, using temporary client ID: ${persistentClientId}`);
    }
    
    // This will store the clientId, which is used to identify the client in the backend
    this.interviewSessionId = persistentClientId;
    
    // Use the backend WebSocket URL instead of directly connecting to Gemini
    this.url = getBackendWsUrl(persistentClientId);
    
    console.log(`[MultimodalLiveClient] Initialized with backend URL: ${this.url}`);
    
    this.send = this.send.bind(this);
    
    // Ensure we have a global registry for tracking WebSocket connections
    if (typeof window !== 'undefined') {
      if (!window.mocktrailWsConnections) {
        window.mocktrailWsConnections = [];
      }
      
      // Clean up any inactive connections from the registry
      if (window.mocktrailWsConnections.length > 0) {
        window.mocktrailWsConnections = window.mocktrailWsConnections.filter(
          socket => socket && (
            socket.readyState === WebSocket.CONNECTING || 
            socket.readyState === WebSocket.OPEN
          )
        );
      }
    }
    
    // Check for existing quota exceeded condition
    if (MultimodalLiveClient.isQuotaExceeded()) {
      console.log('[MultimodalLiveClient] [constructor] Quota exceeded flag is active');
    }
  }

  log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  // Check if the WebSocket is actually connected and ready
  isConnected(): boolean {
    // Simply check the actual WebSocket readyState
    return this.ws?.readyState === WebSocket.OPEN;
  }
  
  // Check if a connection attempt is already in progress
  isConnecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }
  
  // Get the client ID being used for this connection
  getInterviewSessionId(): string {
    return this.interviewSessionId;
  }

  connect(config: LiveConfig): Promise<boolean> {
    // Check if we've exceeded the connection attempt limit
    if (this.connectionAttempts >= 3) {
      console.error(`[MultimodalLiveClient] Maximum connection attempts (${this.connectionAttempts}) reached. Please refresh to try again.`);
      return Promise.resolve(false);
    }
    
    // Use an existing connection if possible
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log(`[MultimodalLiveClient] Already connected, using existing connection`);
      return Promise.resolve(true);
    }
    
    // Check for existing connection with same URL in window registry
    let existingConnection: WebSocket | null = null;
    if (typeof window !== 'undefined' && window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
      existingConnection = window.mocktrailWsConnections.find(socket => 
        socket && 
        socket.url === this.url && 
        (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
      ) || null;
      
      if (existingConnection) {
        console.log(`[MultimodalLiveClient] Found existing connection to ${this.url}, reusing it`);
        
        // If it's in CONNECTING state, wait for it to open
        if (existingConnection.readyState === WebSocket.CONNECTING) {
          console.log(`[MultimodalLiveClient] Existing connection is in CONNECTING state, waiting for it to open`);
          
          return new Promise((resolve) => {
            const openHandler = () => {
              console.log(`[MultimodalLiveClient] Existing connection opened, reusing it`);
              existingConnection!.removeEventListener('open', openHandler);
              this.ws = existingConnection;
              resolve(true);
            };
            
            const errorHandler = () => {
              console.error(`[MultimodalLiveClient] Existing connection failed to open, creating new one`);
              existingConnection!.removeEventListener('error', errorHandler);
              // Continue with creating a new connection
              this._createNewConnection(config).then(resolve);
            };
            
            existingConnection!.addEventListener('open', openHandler);
            existingConnection!.addEventListener('error', errorHandler);
            
            // Set a timeout in case the connection never opens or errors
            setTimeout(() => {
              existingConnection!.removeEventListener('open', openHandler);
              existingConnection!.removeEventListener('error', errorHandler);
              // Continue with creating a new connection
              this._createNewConnection(config).then(resolve);
            }, 5000);
          });
        } else if (existingConnection.readyState === WebSocket.OPEN) {
          // Use the existing connection
          this.ws = existingConnection;
          return Promise.resolve(true);
        }
      }
    }
    
    // Create a new connection
    return this._createNewConnection(config);
  }

  // Helper method to create a new connection
  private _createNewConnection(config: LiveConfig): Promise<boolean> {
    return new Promise((resolve) => {
      this.connectionAttempts += 1;
      console.log(`[MultimodalLiveClient] Creating new WebSocket connection (attempt ${this.connectionAttempts})...`);
      
      this.ws = new WebSocket(this.url);
      
      // Register this connection in the global registry
      if (typeof window !== 'undefined' && window.mocktrailWsConnections) {
        window.mocktrailWsConnections.push(this.ws);
      }
      
      // Add timeout to detect connection failures quickly
      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          console.error(`[MultimodalLiveClient] Connection timeout after 10 seconds`);
          
          // Close the connection attempt if still connecting
          if (this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
          }
          
          resolve(false);
        }
      }, 10000);
      
      this.ws.onopen = () => {
        // Clear timeout since connection succeeded
        clearTimeout(connectionTimeout);
        console.log(`[MultimodalLiveClient] WebSocket connection opened`);
        
        try {
          // CRITICAL: Send config immediately in the open handler
          // The backend expects this as the first message

          // Add implementor_unique_id to interviewDetails if this is an implementor interview
          // @ts-ignore - config has interviewDetails added by use-live-api hook
          const configToSend: any = { ...config };
          if (this.implementorUniqueId && configToSend.interviewDetails) {
            configToSend.interviewDetails = {
              ...configToSend.interviewDetails,
              implementor_unique_id: this.implementorUniqueId
            };
            console.log('[MultimodalLiveClient] Added implementor_unique_id to config:', this.implementorUniqueId);
          }

          const configMessage = JSON.stringify({
            type: 'config',
            config: configToSend,
          });
          
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(configMessage);
            console.log('[MultimodalLiveClient] Config sent to server');
            
            // Keep a copy of the config
            this.config = config;
            
            resolve(true);
          } else {
            console.error('[MultimodalLiveClient] Cannot send config: WebSocket not open');
            resolve(false);
          }
        } catch (err) {
          console.error('[MultimodalLiveClient] Error sending config:', err);
          resolve(false);
        }
      };
      
      this.ws.onmessage = (event) => this.handleServerMessage(event);
      
      this.ws.onerror = (error) => {
        console.error('[MultimodalLiveClient] WebSocket error:', error);
        clearTimeout(connectionTimeout);
        resolve(false);
      };
      
      this.ws.onclose = (event) => {
        console.log(`[MultimodalLiveClient] WebSocket closed: ${event.code} - ${event.reason}`);
        
        // Cleanup global registry on close
        if (typeof window !== 'undefined' && window.mocktrailWsConnections) {
          window.mocktrailWsConnections = window.mocktrailWsConnections.filter(socket => socket !== this.ws);
        }
        
        // Don't resolve here as we already resolved in onopen or onerror
      };
    });
  }

  disconnect() {
    if (this.ws) {
      console.log('[MultimodalLiveClient] Disconnecting WebSocket and stopping audio...');
      
      // First stop all audio resources
      console.log('[MultimodalLiveClient] Stopping all audio resources');
      this.emit('interrupted');
      this.emit('cleanup_audio');
          
      // Reset any audio-related state
      this._lastTranscription = '';
      
      try {
        this.ws.close();
      } catch (error) {
        console.error('[MultimodalLiveClient] Error closing WebSocket:', error);
      }
      this.ws = null;
      this.config = null;
      this.connectionAttempts = 0;
      console.log('[MultimodalLiveClient] WebSocket disconnected and audio stopped');
    } else {
      console.log('[MultimodalLiveClient] No WebSocket connection to disconnect');
    }
  }

  // Send audio data in a format compatible with the backend
  sendAudio(audioData: string) {
    // Check only the actual WebSocket readyState
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('[MultimodalLiveClient] Cannot send audio: WebSocket not open');
      return;
    }

    try {
      const message = {
        type: 'audio',
        data: audioData
      };
      
      this.ws?.send(JSON.stringify(message));
      // Log success periodically to reduce console noise
      if (Math.random() < 0.05) {
        console.log('[MultimodalLiveClient] Audio data sent successfully');
      }
    } catch (error) {
      console.error('[MultimodalLiveClient] Error sending audio:', error);
    }
  }
  
  // Send video/image data in a format compatible with the backend
  sendVideo(imageData: string) {
    // Check only the actual WebSocket readyState
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('[MultimodalLiveClient] Cannot send video: WebSocket not open');
      return;
    }

    try {
      // IMPORTANT: The backend expects 'image' type, not 'video'
      const message = {
        type: 'image',
        data: imageData
      };
      
      this.ws?.send(JSON.stringify(message));
      console.log('[MultimodalLiveClient] Image data sent successfully');
    } catch (error) {
      console.error('[MultimodalLiveClient] Error sending video:', error);
    }
  }
  
  // Send text data in a format compatible with the backend
  sendText(text: string) {
    // Check only the actual WebSocket readyState
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('[MultimodalLiveClient] Cannot send text: WebSocket not open');
      return;
    }

    try {
      const message = {
        type: 'text',
        data: text
      };
      
      this.ws?.send(JSON.stringify(message));
      console.log(`[MultimodalLiveClient] Text sent successfully: "${text.substring(0, 30)}${text.length > 30 ? '...' : ''}"`);
    } catch (error) {
      console.error('[MultimodalLiveClient] Error sending text:', error);
    }
  }

  // Modified sendRealtimeInput to use the correct format
  sendRealtimeInput(data: GenerativeContentBlob[] | { 
    audio?: { mimeType: string, data: string },
    video?: { mimeType: string, data: string },
    text?: string
  }) {
    console.log('[MultimodalLiveClient] sendRealtimeInput called with type:', Array.isArray(data) ? 'array' : 'object');
    
    // Handle object format
    if (!Array.isArray(data)) {
      if (data.audio && data.audio.mimeType && data.audio.data) {
        this.sendAudio(data.audio.data);
      }
      
      if (data.video && data.video.mimeType && data.video.data) {
        this.sendVideo(data.video.data);
      }
      
      if (data.text) {
        console.log(`[MultimodalLiveClient] Sending text input: "${data.text.substring(0, 50)}${data.text.length > 50 ? '...' : ''}"`);
        // Send text with specific log for tracking
        this.log(`client.sendText`, data.text);
        this.sendText(data.text);
      }
      
      return;
    }
    
    // Handle array format
    for (const chunk of data) {
      const mimeType = chunk.mimeType.toLowerCase();
      
      if (mimeType.startsWith('audio/')) {
        this.sendAudio(chunk.data);
      } else if (mimeType.startsWith('image/')) {
        this.sendVideo(chunk.data);
      } else if (mimeType === 'text/plain' && typeof chunk.data === 'string') {
        // Handle text data in array format
        console.log(`[MultimodalLiveClient] Sending text input from array: "${chunk.data.substring(0, 50)}${chunk.data.length > 50 ? '...' : ''}"`);
        this.sendText(chunk.data);
      }
    }
  }

  sendToolResponse(toolResponse: ToolResponseMessage["toolResponse"]) {
    console.log('[MultimodalLiveClient] sendToolResponse not implemented');
  }

  send(parts: Part | Part[], turnComplete: boolean = true) {
    console.log('[MultimodalLiveClient] send not implemented');
  }

  private handleServerMessage(event: MessageEvent) {
    try {
      if (typeof event.data === 'string') {
        const data = JSON.parse(event.data);
        
        // Handle status: connected message
        if (data.status === 'connected') {
          console.log(`[MultimodalLiveClient] Connection established with status: connected`);
        }
        
        // Handle audio data
        if (data.type === 'audio' && data.data) {
          // console.log(`[MultimodalLiveClient] Received audio data, size: ${data.data.length}`);
          const arrayBuffer = base64ToArrayBuffer(data.data);
          this.emit("audio", arrayBuffer);
        }
        
        // Handle text data
        if (data.type === 'text' && data.data) {
          console.log(`[MultimodalLiveClient] Received text: "${data.data.substring(0, 50)}${data.data.length > 50 ? '...' : ''}"`);
          this.emit("content", { 
            modelTurn: { 
              parts: [{ text: data.data }] 
            } 
          });
        }
        
        // Handle text_message data (structured text from AI via function calling)
        if (data.type === 'text_message' && data.data) {
          console.log(`[MultimodalLiveClient] Received text_message: type=${data.data.message_type}, length=${data.data.message?.length || 0}`);
          // Emit as a special content event that includes the structured data
          // Note: Using 'as any' to bypass Part type constraint from Google Generative AI
          this.emit("content", { 
            modelTurn: { 
              parts: [{ 
                text: data.data.message,
                textMessageData: data.data // Include the full structured data
              } as any] 
            } 
          });
        }
        
        // Handle transcription data (AI speech)
        if (data.type === 'transcription' && data.data) {
          // Track if this is a new transcription or continuing transcription
          const isNewTranscription = !this._lastTranscription || data.data.length < this._lastTranscription.length;
          const transcriptionDelta = isNewTranscription ? data.data : 
            data.data.substring(this._lastTranscription?.length || 0);
          
          console.log(`[MultimodalLiveClient] Received transcription: "${data.data.substring(0, 50)}${data.data.length > 50 ? '...' : ''}"${
            isNewTranscription ? ' (new)' : ` (+ "${transcriptionDelta}")`
          }`);
          
          // Save the last transcription for comparison
          this._lastTranscription = data.data;
          
          // Emit the event directly to ensure it's captured properly by all listeners
          this.emit("transcription", data.data);
        }
        
        // Handle output transcription explicitly (matches main.py handling)
        if (data.type === 'outputTranscription' && data.data) {
          console.log(`[MultimodalLiveClient] Received outputTranscription: "${data.data.substring(0, 50)}${data.data.length > 50 ? '...' : ''}"`);
          
          // Emit as a standard transcription event for compatibility
          this.emit("transcription", data.data);
        }
        
        // Handle input transcription (user speech)
        if (data.type === 'input_transcription' || data.type === 'user_transcription' && data.data) {
          console.log(`[MultimodalLiveClient] Received input transcription: "${data.data.substring(0, 50)}${data.data.length > 50 ? '...' : ''}"`);
          
          // Emit the input transcription event
          this.emit("input_transcription", data.data);
          
          // Log this as a significant event
          this.log(`client.input_transcription`, data.data);
          
          // Check for final flag
          if (data.is_final === true) {
            console.log(`[MultimodalLiveClient] Input transcription marked as final`);
          }
        }
        
        // Handle turn complete with enhanced logging
        if (data.type === 'turn_complete') {
          console.log(`[MultimodalLiveClient] Turn complete received, finalizing transcription`);
          
          // Clear the last transcription when turn is complete
          this._lastTranscription = '';
          
          // Emit turn complete event
          this.emit("turncomplete");
        }
        
        // Handle thinking state from backend
        if (data.type === 'thinking') {
          console.log(`[MultimodalLiveClient] Thinking state received: ${data.data}`);
          this.emit("thinking", data.data);
        }
      } else if (event.data instanceof ArrayBuffer) {
        console.log(`[MultimodalLiveClient] Received binary data, size: ${event.data.byteLength}`);
        // Handle binary data if needed
      } else {
        console.log(`[MultimodalLiveClient] Received unexpected data type: ${typeof event.data}`);
      }
    } catch (err) {
      console.warn(`[MultimodalLiveClient] Error handling message:`, err);
      // Try to recover - don't let one parse error break the connection
      try {
        if (typeof event.data === 'string') {
          console.log(`[MultimodalLiveClient] Raw message data: ${event.data.substring(0, 100)}...`);
        }
      } catch (innerErr) {
        console.error(`[MultimodalLiveClient] Failed to log raw message:`, innerErr);
      }
    }
  }

  // Add method to stop all audio resources
  stopAllAudio(): void {
    console.log('[MultimodalLiveClient] Stopping all audio resources');
    
    // Emit an interrupt event to stop any ongoing audio playback
    this.emit('interrupted');
    
    // Also emit a specific cleanup event for audio resources
    this.emit('cleanup_audio');
  }
}
